/**
 * Cobros Ventas x Mayor — migración web de ares9715.
 *
 * Cobra las facturas al por mayor pendientes de un cliente: se elige el
 * cliente, se cargan sus facturas con balance y desde cada una se traen las
 * cuotas pendientes a una lista acumulativa (pueden mezclarse cuotas de
 * varias facturas del mismo cliente). El usuario distribuye manualmente el
 * pago entre las cuotas (se permiten pagos parciales, nunca por encima del
 * pendiente) y al registrar el cobro se imprime el recibo. El recibo
 * multi-factura sale completo con todas sus líneas (el escritorio lo
 * colapsaba).
 */
import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { monedaLabel, normalizaLegacy } from "lib/moneda";
import {
  fmtMoney,
  etiquetaCuota,
  imprimirReciboCobroXmayor,
} from "modules/ventasxmayor/impresionxmayor";

const filtrarClientes = createFilterOptions({
  stringify: (o) => (typeof o === "string" ? o : `${o.descrip || ""} ${o.rnc || ""}`),
  limit: 60,
});

const toNum = (v) => {
  const n = parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

// Compara en centavos enteros: valor y valor_pagado vienen de columnas FLOAT,
// así que un pendiente de 1,073.33 puede llegar como 1073.3299560546875 y el
// monto tecleado (1073.33) "excedía" por milésimas.
const centavos = (v) => Math.round(toNum(v) * 100);
const excede = (monto, pendiente) => centavos(monto) > centavos(pendiente);

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function NoRowsFacturas() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Seleccione un cliente con facturas pendientes
      </Typography>
    </Stack>
  );
}

function NoRowsCuotas() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Cargue las cuotas de una factura con el botón “Cuotas”
      </Typography>
    </Stack>
  );
}

export default function CobrosXmayor({ embedded = false, onExit, onSaved } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [clientes, setClientes] = useState([]);
  const [cargandoClientes, setCargandoClientes] = useState(true);
  const [cliente, setCliente] = useState(null);

  const [facturas, setFacturas] = useState([]);
  const [cargandoFacturas, setCargandoFacturas] = useState(false);

  const [cuotas, setCuotas] = useState([]);
  const [cargandoCuotas, setCargandoCuotas] = useState(0);

  const [tasa, setTasa] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [tipodepago, setTipodepago] = useState("Contado");
  const [referenciapago, setReferenciapago] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    const cargarClientes = async () => {
      setCargandoClientes(true);
      try {
        const { ok, data } = await post("/api/xmayor_clientes", {});
        if (ok) setClientes(data.clientes || []);
        else
          showAlert({
            message: data.Error || "No se pudieron cargar los clientes",
            color: "error",
          });
      } finally {
        setCargandoClientes(false);
      }
    };
    cargarClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarPendientes = async (cli = cliente) => {
    if (!cli) {
      setFacturas([]);
      return;
    }
    setCargandoFacturas(true);
    try {
      const { ok, data } = await post("/api/xmayor_pendientes", { cliente: cli.id });
      if (ok) setFacturas(data.facturas || []);
      else
        showAlert({ message: data.Error || "No se pudieron cargar las facturas", color: "error" });
    } finally {
      setCargandoFacturas(false);
    }
  };

  const limpiarPago = () => {
    setCuotas([]);
    setTasa("");
    setDescripcion("");
    setTipodepago("Contado");
    setReferenciapago("");
  };

  const limpiar = () => {
    setCliente(null);
    setFacturas([]);
    limpiarPago();
  };

  const elegirCliente = (v) => {
    setCliente(v);
    setFacturas([]);
    limpiarPago();
    if (v) cargarPendientes(v);
  };

  // Moneda del recibo: la de la primera factura que entró a la lista. El
  // backend rechaza un recibo que mezcle monedas (valorpagado de la cabecera
  // es la suma de las cuotas, y sumar pesos con dólares no da ninguna de las
  // dos), así que aquí se impide antes de que el usuario teclee importes.
  const monedaRecibo = cuotas.length ? normalizaLegacy(cuotas[0].moneda) : null;

  // Trae las cuotas de una factura a la lista acumulativa (reemplaza las de
  // la misma factura si ya estaban cargadas).
  const cargarCuotas = async (factura) => {
    const monedaFactura = normalizaLegacy(factura.moneda);
    const otras = cuotas.filter((c) => c.id_factura !== factura.id);
    if (otras.length && normalizaLegacy(otras[0].moneda) !== monedaFactura) {
      showAlert({
        message: `Este recibo va en ${monedaLabel(
          otras[0].moneda
        )}. Para cobrar facturas en ${monedaLabel(factura.moneda)} registre un recibo aparte.`,
        color: "warning",
      });
      return;
    }
    setCargandoCuotas(factura.id);
    try {
      const { ok, data } = await post("/api/xmayor_cuotas", { id: factura.id });
      if (ok) {
        // la moneda y la tasa viajan con cada cuota: la cuota se debe en la
        // moneda de SU factura
        const nuevas = (data.cuotas || []).map((c) => ({
          ...c,
          pendiente: centavos(c.pendiente) / 100,
          apagar: "0",
          moneda: factura.moneda,
          tasafactura: factura.tasa,
        }));
        setCuotas((prev) => [...prev.filter((c) => c.id_factura !== factura.id), ...nuevas]);
      } else {
        showAlert({ message: data.Error || "No se pudieron cargar las cuotas", color: "error" });
      }
    } finally {
      setCargandoCuotas(0);
    }
  };

  const setAPagar = (id, valor) => {
    setCuotas((prev) => prev.map((c) => (c.id === id ? { ...c, apagar: valor } : c)));
  };

  const validarAPagar = (row) => {
    if (excede(row.apagar, row.pendiente)) {
      showAlert({
        message: `El valor excede el pendiente de la cuota ${etiquetaCuota(
          row.numero_pago
        )} (${monedaLabel(row.moneda)} ${fmtMoney(row.pendiente)})`,
        color: "warning",
      });
      setAPagar(row.id, "0");
    }
  };

  const quitarCuota = (id) => {
    setCuotas((prev) => prev.filter((c) => c.id !== id));
  };

  const totalAPagar = useMemo(() => cuotas.reduce((acc, c) => acc + toNum(c.apagar), 0), [cuotas]);

  const pagar = async () => {
    const detalle = cuotas
      .filter((c) => toNum(c.apagar) > 0)
      .map((c) => ({ id: c.id, valor: toNum(c.apagar) }));
    if (!cliente || detalle.length === 0) {
      showAlert({
        message: "Debe indicar el valor a pagar de al menos una cuota",
        color: "warning",
      });
      return;
    }
    const excedida = cuotas.find((c) => excede(c.apagar, c.pendiente));
    if (excedida) {
      showAlert({
        message: `El valor excede el pendiente de la cuota ${etiquetaCuota(excedida.numero_pago)}`,
        color: "warning",
      });
      return;
    }
    if (toNum(tasa) <= 0) {
      showAlert({ message: "Debe indicar la tasa del día", color: "warning" });
      return;
    }
    if (!descripcion.trim()) {
      showAlert({ message: "La descripción del cobro no puede estar en blanco", color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/xmayor_pagar", {
        cliente: cliente.id,
        tasa: toNum(tasa),
        descripcion: descripcion.trim(),
        tipodepago,
        referenciapago: referenciapago.trim(),
        detalle,
      });
      if (ok) {
        showAlert({ message: data.ok || "Cobro registrado", color: "success" });
        const recibo = await post("/api/xmayor_recibo", { recibo: data.recibo });
        if (recibo.ok) {
          imprimirReciboCobroXmayor(recibo.data);
        } else {
          showAlert({
            message: recibo.data.Error || "No se pudo cargar el recibo para imprimir",
            color: "error",
          });
        }
        limpiarPago();
        cargarPendientes();
        if (onSaved) onSaved();
      } else {
        showAlert({ message: data.Error || "No se pudo registrar el cobro", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  const totalPendienteCliente = useMemo(
    () => facturas.reduce((acc, f) => acc + toNum(f.balance), 0),
    [facturas]
  );

  const columnasFacturas = [
    { field: "id", headerName: "Fact#", width: 90 },
    { field: "fecha", headerName: "Fecha", width: 110 },
    { field: "cantidad", headerName: "Chasis", width: 90, align: "center", headerAlign: "center" },
    {
      field: "valor",
      headerName: "Valor",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "vplicado",
      headerName: "Pagado",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "balance",
      headerName: "Balance",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "acciones",
      headerName: "",
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="small"
          startIcon={
            cargandoCuotas === params.row.id ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <ReceiptLongOutlinedIcon />
            )
          }
          onClick={() => cargarCuotas(params.row)}
          disabled={cargandoCuotas === params.row.id}
          sx={btnOutline(FP.color.navy)}
        >
          Cuotas
        </Button>
      ),
    },
  ];

  const columnasCuotas = [
    { field: "id_factura", headerName: "Fact#", width: 90 },
    {
      field: "numero_pago",
      headerName: "Cuota#",
      width: 90,
      align: "center",
      headerAlign: "center",
      valueFormatter: (value) => etiquetaCuota(value),
    },
    { field: "fecha", headerName: "Fecha", width: 110 },
    {
      field: "valor",
      headerName: "Valor",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "valor_pagado",
      headerName: "Pagado",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "pendiente",
      headerName: "Pendiente",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "apagar",
      headerName: "A pagar",
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <TextField
          size="small"
          value={params.row.apagar}
          onChange={(e) => setAPagar(params.row.id, e.target.value)}
          onBlur={() => validarAPagar(params.row)}
          inputProps={{ style: { textAlign: "right" } }}
          sx={fpInputSx}
        />
      ),
    },
    {
      field: "acciones",
      headerName: "",
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="small"
          onClick={() => quitarCuota(params.row.id)}
          sx={{ ...btnOutline(FP.color.danger), minWidth: 40, px: 1 }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
        </Button>
      ),
    },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Cobros Ventas x Mayor"
        subtitle="Aplica cobros a las cuotas de las facturas al por mayor pendientes"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
              Nuevo
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Cliente y facturas pendientes */}
        <Box sx={sectionBox}>
          <SectionTitle icon={PersonSearchOutlinedIcon} color={FP.color.accentBlue}>
            Cliente
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarClientes}
                options={clientes}
                loading={cargandoClientes}
                value={cliente}
                getOptionLabel={(o) => (typeof o === "string" ? o : o.descrip || "")}
                isOptionEqualToValue={(o, v) => (o.id || o) === (v.id || v)}
                onChange={(_, v) => elegirCliente(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.descrip}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {o.rnc ? `RNC ${o.rnc}` : ""}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cliente al por mayor (nombre o RNC)"
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
              {cliente && (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    label={`RNC ${cliente.rnc || "—"}`}
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    label={`${facturas.length} facturas — Balance RD$ ${fmtMoney(
                      totalPendienteCliente
                    )}`}
                    sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
                  />
                </Stack>
              )}
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={facturas}
              columns={columnasFacturas}
              loading={cargandoFacturas}
              rowHeight={46}
              pageSizeOptions={[10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              slots={{ noRowsOverlay: NoRowsFacturas }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "180px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>

        {/* Cuotas a pagar */}
        <Box sx={sectionBox}>
          <SectionTitle icon={ReceiptLongOutlinedIcon} color={FP.color.info}>
            Cuotas a pagar
          </SectionTitle>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={cuotas}
              columns={columnasCuotas}
              rowHeight={52}
              pageSizeOptions={[10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              slots={{ noRowsOverlay: NoRowsCuotas }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "180px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>

        {/* Pago */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <SectionTitle icon={PaymentsOutlinedIcon} color={FP.color.success}>
            Pago
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                label={
                  monedaRecibo ? `Total a pagar (${monedaLabel(monedaRecibo)})` : "Total a pagar"
                }
                value={fmtMoney(totalAPagar)}
                InputProps={{ readOnly: true }}
                inputProps={{ style: { textAlign: "right" } }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                label="Tasa"
                value={tasa}
                onChange={(e) => setTasa(e.target.value)}
                placeholder="0.00"
                inputProps={{ style: { textAlign: "right" } }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Descripción"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Tipo de pago"
                value={tipodepago}
                onChange={(e) => setTipodepago(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="Contado">Contado</MenuItem>
                <MenuItem value="Transferencias">Transferencias</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={3}>
              <TextField
                fullWidth
                label="Referencia"
                value={referenciapago}
                onChange={(e) => setReferenciapago(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                startIcon={
                  guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                }
                onClick={pagar}
                disabled={guardando}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Registrar cobro e imprimir
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}
